/**
 * The LV surface runtime — ONE implementation in the harness, consumed by
 * every surface as `../../shell/surfaceRuntime` (the surface contract allows
 * shell imports only). Each surface constructs its own instance:
 *
 *   // src/surfaces/<id>/runtime.ts (synced from the surface repo)
 *   import { createSurfaceRuntime } from '../../shell/surfaceRuntime'
 *   export const { adoptEntryParams, getLmsBase, api, apiSend, hasToken } =
 *     createSurfaceRuntime('inbox')
 *
 * so 11+ surfaces never drift on auth precedence, error shape, or URL hygiene.
 *
 * Bearer precedence (SSO cutover), identical for every surface:
 *   1. The LV shell session (localStorage `lv.session.v1`, read through the
 *      shell's own auth store — same SPA, same module). When a valid,
 *      not-client-side-expired session exists it is the bearer on every API
 *      call: apiFetch attaches it (overriding any legacy header), adopts
 *      sliding-renewal replacements off X-LV-Refreshed-Token, and drops the
 *      session on a server 401 so the shell returns to the login gate. The
 *      legacy #token= handoff is NOT adopted while a session exists —
 *      EXCEPT when the handoff carries a DIFFERENT sub: an identity change
 *      (impersonation, another account) makes the handoff the newer
 *      authority, so the stale session is cleared and the handoff adopted
 *      ("coming in as someone else must not poison the session", 2026-10-10).
 *   2. LEGACY handoff — the LMS redirects the signed-in learner to
 *      /<lv-host>/<route>#token=<jwt>[&from=<lms-base>] — the hash never leaves
 *      the browser. The token is lifted into sessionStorage (survives
 *      refresh, dies with the tab) and stripped from the address bar; `from`
 *      remembers the client's LMS base so classic links can go back.
 *      Fallback only, until the deployment sets LV_LEGACY_HANDOFF=off.
 *
 * Server contract: the surface's API router answers { error } bodies on
 * failure; a 403 with code "nav-disabled" means the deployment's nav settings
 * gate this surface off for the learner — unwrap() surfaces that as a clean,
 * non-retryable "not available" message instead of a bare status.
 */
import { apiFetch, clearSession, decodeClaims, getSession } from './auth'
import { currentLvConfig } from './lvConfig'

/** The surface→shell "open in a new tab" bridge event. A surface can't hold
 *  the shell's openSurface (it mounts as a tab's child, below the launcher),
 *  so it dispatches this instead; LvShell listens and opens the tab exactly
 *  as a launcher pick would — the caller's tab stays open underneath. */
export const OPEN_SURFACE_EVENT = 'lv:open-surface'
export interface OpenSurfaceTabDetail {
  surfaceId: string
  label?: string
  /** The shell's SurfaceOpenTarget contract — string values only. */
  params?: Record<string, string>
}

/** The surface→shell flip bridge (two-sided surfaces): a surface asks the
 *  shell to flip ITS OWN tab to another face — the in-surface flip icon's
 *  channel. The surface learns its tab id from target.tabId. */
export const FLIP_FACE_EVENT = 'lv:flip-face'
export interface FlipFaceDetail {
  tabId: string
  face: string
}
/** Ask the shell to flip the hosting tab to `face` (the shell re-validates
 *  eligibility; an ineligible face is ignored). */
export function flipFace(tabId: string, face: string) {
  window.dispatchEvent(new CustomEvent<FlipFaceDetail>(FLIP_FACE_EVENT, { detail: { tabId, face } }))
}

/** The surface→shell rename bridge: a surface whose content identity changes
 *  as you navigate it (forums: the tab should name the forum you're in) asks
 *  the shell to rename ITS OWN tab. `label: null` restores the surface's own
 *  resolved label (leaving the forum returns the tab to "My Forums"). */
export const RENAME_TAB_EVENT = 'lv:rename-tab'
export interface RenameTabDetail {
  tabId: string
  label: string | null
}
/** Ask the shell to rename the hosting tab (no new tab, params untouched). */
export function renameTab(tabId: string, label: string | null) {
  window.dispatchEvent(new CustomEvent<RenameTabDetail>(RENAME_TAB_EVENT, { detail: { tabId, label } }))
}

/** Ask the shell to open a surface in a NEW tab, leaving the caller's tab
 *  alone. `params` ride the tab down as the surface's `target.params` — the
 *  same contract as a launcher-flyout pick (`params.menu` scopes the
 *  connections surface; `params.f`/`params.t` deep-link My Forums). */
export function openSurfaceTab(surfaceId: string, label?: string, params?: Record<string, string>): void {
  try {
    window.dispatchEvent(new CustomEvent<OpenSurfaceTabDetail>(OPEN_SURFACE_EVENT, { detail: { surfaceId, label, params } }))
  } catch { /* no window (SSR/tests) — the open is a nicety, never fatal */ }
}

export interface SurfaceRuntime {
  adoptEntryParams: () => void
  getLmsBase: () => string | null
  api: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) => Promise<T>
  apiSend: <T>(path: string, method: 'POST' | 'PUT' | 'DELETE', body?: unknown) => Promise<T>
  hasToken: () => boolean
}

export function createSurfaceRuntime(surfaceId: string): SurfaceRuntime {
  const API_BASE = `/LearnerView/api/surfaces/${surfaceId}`
  const TOKEN_KEY = `lv-${surfaceId}-token`
  const FROM_KEY = `lv-${surfaceId}-from`

  let legacyToken: string | null = null
  let lmsBase: string | null = null

  function adoptEntryParams(): void {
    try {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const urlToken = hash.get('token')
      const from = hash.get('from')
      if (urlToken) {
        // Never leave a JWT sitting in the address bar, adopted or not.
        hash.delete('token')
        const rest = hash.toString()
        history.replaceState(null, '', window.location.pathname + (rest ? `#${rest}` : ''))
        const existing = getSession()
        // IDENTITY ROTATION: a handoff carrying a DIFFERENT sub than the
        // stored session is the newer authority (the LMS impersonated
        // someone, or another account signed in over this one). The old
        // rule — never adopt while a session exists — let the stale session
        // shadow the handoff identity; clear it and adopt instead.
        const incomingSub = decodeClaims(urlToken)?.sub
        const conflict = !!existing && !!incomingSub && incomingSub !== existing.claims.sub
        if (!existing || conflict) {
          if (conflict) clearSession()
          try {
            sessionStorage.setItem(TOKEN_KEY, urlToken)
            if (from) sessionStorage.setItem(FROM_KEY, from)
          } catch { /* storage blocked — in-memory only */ }
          legacyToken = urlToken
        }
        if (from) lmsBase = from
        return
      }
      legacyToken = sessionStorage.getItem(TOKEN_KEY)
      lmsBase = sessionStorage.getItem(FROM_KEY)
    } catch { /* storage blocked and no URL token — unauthenticated view */ }
  }

  function getLmsBase(): string | null {
    const base = lmsBase ?? (typeof currentLvConfig()?.lmsBaseUrl === 'string' ? currentLvConfig()!.lmsBaseUrl! : null)
    if (!base) return null
    return base.endsWith('/') ? base : base + '/'
  }

  async function unwrap<T>(r: Response): Promise<T> {
    if (!r.ok) {
      let msg = `${r.status}`
      let code: string | undefined
      try {
        const b = (await r.json()) as { error?: string; code?: string }
        if (b?.error) msg = b.error
        code = b?.code
      } catch { /* keep status */ }
      if (code === 'nav-disabled') msg = 'This page is not available on this deployment.'
      const err = new Error(msg) as Error & { status?: number; code?: string }
      err.status = r.status
      err.code = code
      throw err
    }
    return r.json() as Promise<T>
  }

  // The legacy token rides as a plain header; when a shell session exists
  // apiFetch overrides it with the session bearer — precedence lives in one place.
  const legacyHeaders = (): Record<string, string> => (legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {})

  async function api<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    const url = new URL(API_BASE + path, window.location.origin)
    if (params) for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
    }
    const r = await apiFetch(url.toString(), { headers: legacyHeaders() })
    return unwrap<T>(r)
  }

  async function apiSend<T>(path: string, method: 'POST' | 'PUT' | 'DELETE', body?: unknown): Promise<T> {
    const r = await apiFetch(API_BASE + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...legacyHeaders(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    return unwrap<T>(r)
  }

  function hasToken(): boolean {
    return !!getSession() || !!legacyToken
  }

  return { adoptEntryParams, getLmsBase, api, apiSend, hasToken }
}
